import { API_URL } from '@/constants/config';
import { encodeQuery } from '~/modules/common/utils/routingUtils';
import { getAuthHeader } from '~/modules/common/utils/cookieUtils';

const base = API_URL + '/v3.5';

/**
 * Profile Service
 *
 * @param {object} http axios instance from nuxt context
 * @param {object} cookies cookies from nuxt context
 */
export const profileService = (http, cookies) => ({
  /**
   * Get User
   *
   * @param {string} [username]
   */
  async getUser(username) {
    const { data } = await http.$get(`${base}/users/${username}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get Blocked Users List
   *
   */
  async blockedUsersList() {
    const { data } = await http.$get(`${base}/social/me/blocked`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get User Followers
   *
   * @param {object} params object
   * @param {string} [params.userId]
   * @param {number} [params.page]
   * @param {boolean} [params.check]
   */
  async getUserFollowers({ userId, page, check = false }) {
    const queryParams = encodeQuery({ page, check });
    const { data } = await http.$get(`${base}/social/${userId}/followers${queryParams}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get User Followings
   *
   * @param {object} params object
   * @param {string} [params.userId]
   * @param {number} [params.page]
   * @param {boolean} [params.check]
   */
  async getUserFollowing({ userId, page, check = false }) {
    const queryParams = encodeQuery({ page, check });
    const { data } = await http.$get(`${base}/social/${userId}/following${queryParams}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get User Articles
   *
   * @param {object} params object
   * @param {string} [params.userId]
   * @param {number} [params.page]
   */
  async getUserArticles({ userId, page, limit }) {
    const queryParams = encodeQuery({ page, limit, categories: true, breadcrumb: true });
    const { data, pagination } = await http.$get(`${base}/articles/user/${userId}${queryParams}`);
    return { data, pagination };
  },

  /**
   * Delete article by id
   * @param {string} id article id
   */
  async deleteArticle(id) {
    const { data } = await http.$delete(`${base}/articles/${id}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get User Favorites
   *
   * @param {object} params object
   * @param {string} [params.userId]
   * @param {number} [params.page]
   */
  async getUserFavorites({ userId, page }) {
    const queryParams = encodeQuery({ page, categories: true, breadcrumb: true });
    const { data } = await http.$get(`${base}/favorites/${userId}${queryParams}`);
    return { data };
  },

  /**
   * Get User Comments
   *
   * @param {object} params object
   * @param {string} [params.userId]
   * @param {number} [params.page]
   */
  async getUserComments({ userId, page }) {
    const queryParams = encodeQuery({ page, userId, useHasNextPage: true });
    const { data, pagination } = await http.$get(`${base}/comments${queryParams}`);
    return { data, pagination };
  },

  /**
   * Get Country List
   */
  async getCountryList() {
    const { data } = await http.$get(`${base}/metadata/countries`);
    return { data };
  },
});
